import React from 'react';

/**
 * 테이블 정렬 로직을 제공한다.
 *
 * @param {Array} preprocessedData - 전처리된 데이터 배열
 * @param {boolean} isDataFullyLoaded
 * @param {{ key: string|null, direction: string }} sortConfig
 * @param {function} setSortConfig
 * @param {string[]} sortableColumns - 정렬 가능한 컬럼 목록
 * @returns {{ sortedData: Array, isSortingAvailable: boolean, requestSort: function, getSortIndicator: function, resetSort: function }}
 */
export function useTableSort(preprocessedData, isDataFullyLoaded, sortConfig, setSortConfig, sortableColumns) {
  const isSortingAvailable = React.useMemo(() => {
    return preprocessedData && preprocessedData.length > 0;
  }, [preprocessedData]);

  const sortedData = React.useMemo(() => {
    if (!isDataFullyLoaded) return [];

    const sortableItems = [...preprocessedData];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] === null) return 1;
        if (b[sortConfig.key] === null) return -1;

        const aValue = typeof a[sortConfig.key] === 'string' && !isNaN(a[sortConfig.key].replace(/,/g, ''))
          ? parseFloat(a[sortConfig.key].replace(/,/g, ''))
          : a[sortConfig.key];
        const bValue = typeof b[sortConfig.key] === 'string' && !isNaN(b[sortConfig.key].replace(/,/g, ''))
          ? parseFloat(b[sortConfig.key].replace(/,/g, ''))
          : b[sortConfig.key];

        if (aValue === bValue) {
          return (a.apartment_name || '').localeCompare(b.apartment_name || '');
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [preprocessedData, isDataFullyLoaded, sortConfig]);

  const requestSort = (key) => {
    if (!isSortingAvailable) return;

    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (column) => {
    if (!isSortingAvailable) return '';

    if (sortConfig.key === column) {
      return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    }
    return sortableColumns.includes(column) ? ' ⋮⋮' : '';
  };

  const resetSort = () => {
    setSortConfig({ key: null, direction: 'ascending' });
  };

  return { sortedData, isSortingAvailable, requestSort, getSortIndicator, resetSort };
}
